import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserTasks } from "../features/tasks/taskThunks";
import { FaEye, FaDownload, FaEdit, FaPlus, FaFileUpload, FaTrash } from "react-icons/fa";
import TaskPageHeader from "./common/TaskPageHeader";
import CustomButton from "./Custom/CustomButton";
import "./TaskBoard.css";
import TaskDetailsModal from "./TaskDetailsModal";
import AddTaskModal from "./AddTaskModal";
import BulkUploadModal from "./BulkUploadModal";
import TaskStatusModal from "./TaskStatusModal";
import axiosInstance from "../api/axiosInstance";
import AppDataTable from "./common/AppDataTable";
import AppDrawer from "./common/AppDrawer";
import UserAvatarTooltip from "./common/UserAvatarTooltip";
import { toast } from "react-toastify";
import useTableUrlState from "../hooks/useTableUrlState";
import useFilterFacets from "../hooks/useFilterFacets";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
    statusCards,
    priorityFilterOptions,
    ageingFilterOptions,
    badgeClassLookup,
    formatDate,
    resolveTaskStartDate,
    resolveTaskCompletionTimestamp,
    calculateAgeingDisplay,
    resolveTaskTicketId,
    resolveTaskProjectName,
} from "../utils/taskUtils";

function formatAgeing(minutes) {
    if (minutes == null) return "-";

    const days = Math.floor(minutes / (60 * 24));
    const hours = Math.floor((minutes % (60 * 24)) / 60);
    const mins = minutes % 60;

    let result = [];

    if (days) result.push(`${days}d`);
    if (hours) result.push(`${hours}h`);
    if (mins) result.push(`${mins}m`);

    return result.length > 0 ? result.join(" ") : "0m";
}

/** Staged / URL column filter `id` → `/api/task/me` query parameter name. */
function taskMeQueryParamName(filterId) {
    switch (filterId) {
        case "department":
            return "departmentId";
        case "project":
            return "projectId";
        case "group_id":
            return "groupId";
        case "ageing":
            return "overdue";
        case "due_date_start":
            return "startDate";
        case "due_date_end":
            return "endDate";
        default:
            return filterId;
    }
}

const UUID_QUERY_PARAMS = new Set([
    "departmentId",
    "projectId",
    "groupId",
    "assignedBy",
    "employeeId",
]);

// ─────────────────────────────────────────────────────────────────────────────

const TaskBoard = () => {
    const {
        pagination,
        sorting,
        globalFilter,
        columnFilters,
        onPaginationChange,
        onSortingChange,
        onGlobalFilterChange,
        onColumnFiltersChange,
        applyFiltersAndSearch,
    } = useTableUrlState({ pageSize: 10, persistenceKey: 'my_tasks' });

    // ── Redux task state ──────────────────────────────────────────────────────
    const dispatch = useDispatch();
    const { tasks: rawTasks, total: totalRows, loading: isLoading } = useSelector(
        (state) => state.tasks
    );
    const tasks = Array.isArray(rawTasks) ? rawTasks : [];

    const [isRefetching, setIsRefetching] = useState(false);
    const [userId, setUserId] = useState(null);
    const [currentUserInfo, setCurrentUserInfo] = useState(null);
    const [rowSelection, setRowSelection] = useState({});

    // ── UI visibility ─────────────────────────────────────────────────────────
    const [showTaskDrawer, setShowTaskDrawer] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [refreshSignal, setRefreshSignal] = useState(0);
    const [showAddTask, setShowAddTask] = useState(false);
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [bulkCompleteLoading, setBulkCompleteLoading] = useState(false);
    const [showBulkCompleteModal, setShowBulkCompleteModal] = useState(false);
    const [bulkCompleteTasks, setBulkCompleteTasks] = useState([]);

    // ── Status modal ──────────────────────────────────────────────────────────
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [actionTask, setActionTask] = useState(null);
    const [statusCounts, setStatusCounts] = useState(null);

    // ── Delegate options ──────────────────────────────────────────────────────
    const [delegateOptions, setDelegateOptions] = useState([]);
    const [isExporting, setIsExporting] = useState(false);

    // ── Staged Filters ────────────────────────────────────────────────────────
    const [stagedFilters, setStagedFilters] = useState({});
    const [stagedSearch, setStagedSearch] = useState("");

    // ── Facets ────────────────────────────────────────────────────────────────
    // Build the selected-ID payload from staged filters so facets refresh on
    // every dropdown change (before the user hits "Apply").
    const facetSelectedIds = {
        department_id: stagedFilters.department || "",
        project_id:    stagedFilters.project    || "",
        group_id:      stagedFilters.group_id   || "",
        employee_id:   stagedFilters.assignedBy || "",
    };
    const { facetOptions } = useFilterFacets("my", facetSelectedIds);


    // Migration mapping for legacy URL filters to Task API standard
    const legacyMap = {
        departmentId: "department",
        department_id: "department",
        projectId: "project",
        project_id: "project",
        groupId: "group_id",
        employee: "assignedBy",
        employee_id: "assignedBy",
        overdue: "ageing",
        startDate: "due_date_start",
        endDate: "due_date_end",
    };

    useEffect(() => {
        if (columnFilters.length > 0 || globalFilter) {
            const initial = {};
            columnFilters.forEach((f) => {
                const newKey = legacyMap[f.id] || f.id;
                initial[newKey] = f.value;
            });
            setStagedFilters(initial);
            setStagedSearch(globalFilter);
        }
    }, [columnFilters, globalFilter]); // Sync staged UI when URL state changes (e.g. restoration)

    // Derived header-filter values for UI populating
    const currentDept = stagedFilters.department || "";
    const currentProject = stagedFilters.project || "";
    const currentGroup = stagedFilters.group_id || "";
    const currentEmp = stagedFilters.assignedBy || "";
    const currentAgeing = stagedFilters.ageing || "";
    const currentPrio = stagedFilters.priority || "";
    const currentStartDate = stagedFilters.due_date_start || "";
    const currentEndDate = stagedFilters.due_date_end || "";
    const currentStatus = stagedFilters.status || "All";

    // ── Redux selectors ───────────────────────────────────────────────────────
    const departments = useSelector((state) => state.departments.departments) || [];
    const projects = useSelector((state) => state.projects.projects) || [];
    const groups = useSelector((state) => state.groups.groups) || [];
    const reduxUser = useSelector((state) => state.auth.user);
    const reduxEmployees = useSelector((state) => state.employees.employees) || [];

    // ── Resolve current user ──────────────────────────────────────────────────
    useEffect(() => {
        if (reduxUser) {
            setUserId(reduxUser.userId || reduxUser.id || reduxUser.sub);
            setCurrentUserInfo(reduxUser);
        }
    }, [reduxUser]);

    // ── Build delegate options from employees list ────────────────────────────
    useEffect(() => {
        const items = Array.isArray(reduxEmployees) ? reduxEmployees : [];
        setDelegateOptions(
            items
                .map((item) => {
                    const id = item.userId || item.id;
                    if (!id) return null;
                    const label =
                        `${item.FirstName || ""} ${item.LastName || ""}`.trim() ||
                        item.email ||
                        item.Email;
                    return {
                        value: String(id),
                        label,
                        meta: { email: item.email || item.Email },
                        searchText: label.toLowerCase(),
                    };
                })
                .filter(Boolean)
        );
    }, [reduxEmployees]);

    // ── Append current user to delegate list if missing ───────────────────────
    // Guard inside setter avoids the infinite-loop anti-pattern.
    useEffect(() => {
        if (!userId || !currentUserInfo) return;
        const selfId = String(userId);
        setDelegateOptions((prev) => {
            if (prev.some((o) => o.value === selfId)) return prev;
            const label =
                `${currentUserInfo.FirstName || ""} ${currentUserInfo.LastName || ""}`.trim();
            return [
                ...prev,
                { value: selfId, label, meta: { email: currentUserInfo.email }, searchText: label.toLowerCase() },
            ];
        });
    }, [userId, currentUserInfo]);

    // ── Export tasks ──────────────────────────────────────────────────────────
    const handleExportTasks = async () => {
        setIsExporting(true);
        try {
            const response = await axiosInstance.get("/api/task/me/export", {
                responseType: "blob",
            });
            const disposition = response.headers?.["content-disposition"] || "";
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            const filename = match ? match[1].replace(/['"]/g, "") : "my_tasks_export.xlsx";
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Tasks exported successfully!");
        } catch (err) {
            toast.error(err.message || "Failed to export tasks. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    // ── Status filter helper ──────────────────────────────────────────────────
    const setStatusFilter = (val) => {
        const nextStatus = val === "All" ? "" : val;
        // Keep the staged UI state in sync.
        setStagedFilters((prev) => ({
            ...prev,
            status: nextStatus,
        }));
        // Apply status immediately so user doesn't need to click "Apply".
        onColumnFiltersChange((prev) => [
            ...prev.filter((f) => f.id !== "status"),
            ...(nextStatus ? [{ id: "status", value: nextStatus }] : []),
        ]);
    };

    // ── Fetch tasks ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!userId) return; // Guard against initial fetch before user is resolved
        let cancelled = false;
        const fetchTasks = async () => {
            setIsRefetching(true);
            try {
                // Build params object for the /me endpoint
                const params = {
                    page: pagination.pageIndex + 1,
                    limit: pagination.pageSize
                };

                // Search
                if (globalFilter) params.search = globalFilter;

                // Sorting (align with `/api/task/me`: sortBy, sortOrder)
                if (sorting?.length) {
                    let sortBy = sorting[0].id;
                    if (sortBy === "due_date") sortBy = "due_date";
                    params.sortBy = sortBy;
                    params.sortOrder = sorting[0].desc ? "desc" : "asc";
                }

                // Column filters → flat query params (OpenAPI: status, priority, projectId, …)
                if (columnFilters && columnFilters.length > 0) {
                    columnFilters.forEach((f) => {
                        let val = f.value;
                        if (typeof val === "object" && val !== null) {
                            val = val.id || val.userId || val.value || "";
                        }

                        if ((f.id === "priority" || f.id === "ageing") && typeof val === "string") {
                            val = val.toLowerCase();
                        }

                        if (val !== "All" && val !== "") {
                            const paramName = taskMeQueryParamName(f.id);
                            params[paramName] = UUID_QUERY_PARAMS.has(paramName)
                                ? String(val)
                                : val;
                        }
                    });
                }

                dispatch(fetchUserTasks(params)).finally(() => {
                    if (!cancelled) setIsRefetching(false);
                });
            } catch (error) {
                if (!cancelled) console.error("Task fetch error:", error);
            } finally {
                if (!cancelled) { /* Redux handles loading state */ }
            }
        };

        fetchTasks();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, pagination.pageIndex, pagination.pageSize, sorting, globalFilter, columnFilters, refreshSignal]);

    // ── Fetch status counts for status pills ──────────────────────────────────
    const fetchStatusCounts = useCallback(async (filterParams = {}) => {
        try {
            const { data } = await axiosInstance.get("/api/task/getStatusCount", { params: filterParams });
            const nextCounts = data?.data && typeof data.data === "object" ? data.data : null;
            setStatusCounts(nextCounts);
        } catch (error) {
            setStatusCounts(null);
            console.error("Failed to fetch task status counts:", error);
        }
    }, []);

    useEffect(() => {
        if (!userId) return;
        fetchStatusCounts();
    }, [userId, refreshSignal, fetchStatusCounts]);

    // ── Action handlers ───────────────────────────────────────────────────────
    const handleActionClick = (task) => {
        setActionTask(task);
        setShowStatusModal(true);
    };

    const handleViewTask = (task) => {
        setSelectedTask(task);
        setShowTaskDrawer(true);
    };

    const handleDeleteTask = async (task) => {
        const title = task.title || "this task";
        if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) return;
        try {
            // Updated to use bulk API for single deletion as requested
            await axiosInstance.delete(`/api/task/bulk`, {
                data: {
                    taskIds: [task.id],
                    currentUserId: userId
                },
            });
            toast.success("Task deleted successfully");
            setRefreshSignal(prev => prev + 1);
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || "Failed to delete task");
        }
    };

    // ── Bulk Complete ─────────────────────────────────────────────────────────
    const handleBulkComplete = () => {
        const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
        if (selectedIds.length === 0) return;
        setBulkCompleteTasks(tasks.filter((t) => selectedIds.includes(String(t.id))));
        setShowBulkCompleteModal(true);
    };

    const executeBulkComplete = async () => {
        setBulkCompleteLoading(true);
        setShowBulkCompleteModal(false);
        const ids = bulkCompleteTasks.map((t) => t.id);
        try {
            // Bulk update API: POST /api/task/bulk with taskIds and status
            await axiosInstance.post("/api/task/bulk", {
                taskIds: ids,
                status: "Completed",
                currentUserId: userId
            });
            const msg = `${ids.length} task${ids.length !== 1 ? "s" : ""} completed`;
            toast.success(msg);
            setRefreshSignal(prev => prev + 1);
            setRowSelection({});
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || "Bulk complete failed. Please try again.");
        } finally {
            setBulkCompleteLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} task(s)? This cannot be undone.`)) return;

        setBulkCompleteLoading(true);
        try {
            // Bulk delete API: DELETE /api/task/bulk with taskIds in payload
            await axiosInstance.delete("/api/task/bulk", {
                data: {
                    taskIds: selectedIds,
                    currentUserId: userId
                },
            });
            toast.success(`${selectedIds.length} tasks deleted successfully`);
            setRefreshSignal(prev => prev + 1);
            setRowSelection({});
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || "Bulk delete failed. Please try again.");
        } finally {
            setBulkCompleteLoading(false);
        }
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns = useMemo(
        () => [
            {
                accessorKey: "title",
                header: "Task Name",
                size: 180,
                minSize: 150,
                maxSize: 220,
                muiTableBodyCellProps: {
                    sx: { maxWidth: 180, width: 180, verticalAlign: "middle" },
                },
                muiTableHeadCellProps: {
                    sx: { maxWidth: 180, width: 180 },
                },
                Cell: ({ row }) => {
                    const t = row.original;
                    const ticket = resolveTaskTicketId(t);
                    return (
                        <div className="taskboard-task-name-cell">
                            {ticket ? (
                                <div className="taskboard-task-name-ticket">{ticket}</div>
                            ) : null}
                            <div className="taskboard-task-name-title">
                                {t.title || "—"}
                            </div>
                        </div>
                    );
                },
            },
            {
                id: "project",
                accessorFn: (row) => resolveTaskProjectName(row),
                header: "Project",
                size: 150,
                minSize: 120,
                enableSorting: false,
                Cell: ({ row }) => {
                    const name = resolveTaskProjectName(row.original);
                    if (!name) return <span className="taskboard-meta-muted">—</span>;
                    return <span className="taskboard-project-name">{name}</span>;
                },
            },
            {
                accessorKey: "priority",
                header: "Priority",
                size: 110,
                enableSorting: false,
                Cell: ({ row }) => {
                    const raw = (row.original.priority || "").toString().trim();
                    if (!raw) return <span className="taskboard-meta-muted">—</span>;
                    const key = raw.toLowerCase();
                    const label =
                        priorityFilterOptions.find((o) => o.value === key)?.label ||
                        raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
                    const pillSuffix = { urgent: "urgent", high: "high", medium: "medium", low: "low" }[key] || "default";
                    return (
                        <span className={`taskboard-priority-pill taskboard-priority-${pillSuffix}`}>
                            {label}
                        </span>
                    );
                },
            },
            {
                accessorKey: "due_date",
                header: "TGT",
                size: 100,
                Cell: ({ row }) => formatDate(row.original.due_date || row.original.dueDate),
            },
            {
                id: "ageing",
                header: "Ageing",
                size: 100,
                Cell: ({ row }) => formatAgeing(row.original.ageing),
            },
            {
                accessorKey: "status",
                header: "Status",
                size: 100,
                enableSorting: false,
                Cell: ({ cell }) => (
                    <span className={`taskboard-status-badge ${badgeClassLookup[cell.getValue()?.toLowerCase()] || "Pending"}`}>
                        {cell.getValue()}
                    </span>
                ),
            },
            {
                id: "givenBy",
                header: "Given By",
                size: 64,
                Cell: ({ row }) => {
                    const t = row.original;
                    const firstName = t.assignedBy?.firstName || t.GivenByFirstName || "";
                    const lastName = t.assignedBy?.lastName || t.GivenByLastName || "";
                    const email = t.assignedBy?.email || t.GivenByEmail || t.email || "";
                    return (
                        <Box className="taskboard-avatar-cell" sx={{ display: "flex", alignItems: "center" }}>
                            <UserAvatarTooltip
                                firstName={firstName}
                                lastName={lastName}
                                email={email}
                            />
                        </Box>
                    );
                },
            },
        ],
        []
    );

    // ── Active Pills Logic ──────────────────────────────────────────────────
    const activePills = useMemo(() => {
        const pills = [];

        if (globalFilter) {
            pills.push({
                id: "search",
                label: `Search: "${globalFilter}"`,
                onDelete: () => {
                    setStagedSearch("");
                    onGlobalFilterChange("");
                },
            });
        }

        columnFilters.forEach((f) => {
            // Skip status pill as it is already visible in the category buttons row
            if (f.id === "status") return;

            if (f.id === "department") {
                const dept = departments.find((d) => String(d.id) === f.value);
                if (dept) {
                    pills.push({
                        id: "department",
                        label: `Dept: ${dept.name}`,
                        onDelete: () => {
                            const next = columnFilters.filter((cf) => cf.id !== "department");
                            onColumnFiltersChange(next);
                            setStagedFilters((prev) => ({ ...prev, department: "" }));
                        },
                    });
                }
            } else if (f.id === "project") {
                const proj = projects.find((p) => String(p.id) === f.value);
                if (proj) {
                    pills.push({
                        id: "project",
                        label: `Proj: ${proj.name}`,
                        onDelete: () => {
                            const next = columnFilters.filter((cf) => cf.id !== "project");
                            onColumnFiltersChange(next);
                            setStagedFilters((prev) => ({ ...prev, project: "" }));
                        },
                    });
                }
            } else if (f.id === "group_id") {
                const grp = groups.find((g) => String(g.id) === f.value);
                if (grp) {
                    pills.push({
                        id: "group",
                        label: `Group: ${grp.groupName}`,
                        onDelete: () => {
                            const next = columnFilters.filter((cf) => cf.id !== "group_id");
                            onColumnFiltersChange(next);
                            setStagedFilters((prev) => ({ ...prev, group_id: "" }));
                        },
                    });
                }
            } else if (f.id === "assignedBy") {
                const emp = reduxEmployees.find((e) => String(e.userId || e.id) === f.value);
                if (emp) {
                    pills.push({
                        id: "assignedBy",
                        label: `Given By: ${emp.FirstName || ""} ${emp.LastName || ""}`.trim(),
                        onDelete: () => {
                            const next = columnFilters.filter((cf) => cf.id !== "assignedBy");
                            onColumnFiltersChange(next);
                            setStagedFilters((prev) => ({ ...prev, assignedBy: "" }));
                        },
                    });
                }
            } else if (f.id === "priority") {
                pills.push({
                    id: "priority",
                    label: `Prio: ${f.value}`,
                    onDelete: () => {
                        const next = columnFilters.filter((cf) => cf.id !== "priority");
                        onColumnFiltersChange(next);
                        setStagedFilters((prev) => ({ ...prev, priority: "" }));
                    },
                });
            } else if (f.id === "ageing") {
                const opt = ageingFilterOptions.find((o) => o.value === f.value);
                if (opt) {
                    pills.push({
                        id: "ageing",
                        label: `Ageing: ${opt.label}`,
                        onDelete: () => {
                            const next = columnFilters.filter((cf) => cf.id !== "ageing");
                            onColumnFiltersChange(next);
                            setStagedFilters((prev) => ({ ...prev, ageing: "" }));
                        },
                    });
                }
            } else if (f.id === "due_date_start" || f.id === "due_date_end") {
                const isStart = f.id === "due_date_start";
                const otherId = isStart ? "due_date_end" : "due_date_start";
                const otherVal = columnFilters.find(cf => cf.id === otherId)?.value;
                if (!pills.find(p => p.id === "date_range")) {
                    const startVal = isStart ? f.value : otherVal;
                    const endVal = isStart ? otherVal : f.value;
                    let rangeLabel = "";
                    if (startVal && endVal) rangeLabel = `Date: ${startVal} to ${endVal}`;
                    else if (startVal) rangeLabel = `From: ${startVal}`;
                    else if (endVal) rangeLabel = `To: ${endVal}`;

                    pills.push({
                        id: "date_range",
                        label: rangeLabel,
                        onDelete: () => {
                            const next = columnFilters.filter((cf) => cf.id !== "due_date_start" && cf.id !== "due_date_end" && cf.id !== "startDate" && cf.id !== "endDate");
                            onColumnFiltersChange(next);
                            setStagedFilters((prev) => ({ ...prev, due_date_start: "", due_date_end: "" }));
                        },
                    });
                }
            }
        });

        return pills;
    }, [globalFilter, columnFilters, departments, projects, groups, reduxEmployees, onGlobalFilterChange, onColumnFiltersChange]);

    const renderRowActions = ({ row }) => {
        const t = row.original;
        const selfId = String(userId);
        const assignedById = String(t.assignedBy?.userId || t.assignedBy?.id || t.GivenByUserId || "");
        // User can only delete if they are the one who assigned the task (to themselves or others)
        const canDelete = assignedById === selfId;

        return (
            <Box sx={{ display: "flex", gap: "2px", flexWrap: "nowrap", alignItems: "center" }}>
                <Tooltip title="View Details">
                    <IconButton
                        onClick={() => handleViewTask(t)}
                        size="small"
                        sx={{
                            color: "#64748b",
                            "&:hover": {
                                color: "#2563eb",
                                backgroundColor: "#eff6ff",
                            },
                        }}
                    >
                        <FaEye size={14} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Update Task">
                    <IconButton
                        onClick={() => handleActionClick(t)}
                        size="small"
                        sx={{
                            color: "#64748b",
                            "&:hover": {
                                color: "#d97706",
                                backgroundColor: "#fffbeb",
                            },
                        }}
                    >
                        <FaEdit size={14} />
                    </IconButton>
                </Tooltip>
                <Tooltip title={canDelete ? "Delete Task" : "Disabled (Assigned by someone else)"}>
                    <span> {/* Span wrap for tooltip to work on disabled buttons */}
                        <IconButton
                            onClick={() => handleDeleteTask(t)}
                            disabled={!canDelete}
                            size="small"
                            sx={{
                                color: "#64748b",
                                "&:hover": {
                                    color: "#dc2626",
                                    backgroundColor: "#fef2f2",
                                },
                                "&.Mui-disabled": {
                                    color: "#e2e8f0",
                                }
                            }}
                        >
                            <FaTrash size={14} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <div
                className="task-page-container taskboard-container"
                style={{ height: "calc(100vh - 32px)", display: "flex", flexDirection: "column" }}
            >
                <TaskPageHeader
                    title="My Tasks"
                    buttons={[
                        {
                            label: "Export All",
                            icon: <FaDownload size={14} />,
                            onClick: handleExportTasks,
                            loading: isExporting,
                            loadingText: "Exporting...",
                            color: "inherit",
                            sx: { backgroundColor: '#f1f3f5', color: '#111827', border: '1px solid #e0e4e8', '&:hover': { backgroundColor: '#e5e7eb', borderColor: '#d1d5db' } }
                        },
                        {
                            label: "Import Bulk Tasks",
                            icon: <FaFileUpload size={14} />,
                            onClick: () => setShowBulkUpload(true),
                            disabled: isLoading || isRefetching,
                            color: "inherit",
                            sx: { backgroundColor: '#f1f3f5', color: '#111827', border: '1px solid #e0e4e8', '&:hover': { backgroundColor: '#e5e7eb', borderColor: '#d1d5db' } }
                        },
                        {
                            label: "Create Task",
                            icon: <FaPlus size={14} />,
                            onClick: () => setShowAddTask(true),
                            disabled: isLoading || isRefetching,
                            color: "error"
                        },
                    ]}
                    statusSummary={statusCards}
                    currentStatus={currentStatus}
                    onStatusFilter={setStatusFilter}
                    tasks={tasks}
                    statusCounts={statusCounts}
                    isApplying={isLoading || isRefetching}
                    filters={{
                        departments: {
                            // Prefer facet options (contextual); fall back to all departments if facets not yet loaded
                            options: facetOptions.departments.length > 0 ? facetOptions.departments : departments,
                            value: (
                                facetOptions.departments.find((d) => String(d.id) === currentDept) ||
                                departments.find((d) => String(d.id) === currentDept) ||
                                null
                            ),
                        },
                        projects: {
                            options: facetOptions.projects.length > 0 ? facetOptions.projects : projects,
                            value: (
                                facetOptions.projects.find((p) => String(p.id) === currentProject) ||
                                projects.find((p) => String(p.id) === currentProject) ||
                                null
                            ),
                        },
                        groups: {
                            options: facetOptions.groups.length > 0 ? facetOptions.groups : groups,
                            value: (
                                facetOptions.groups.find((g) => String(g.id) === currentGroup) ||
                                groups.find((g) => String(g.id) === currentGroup) ||
                                null
                            ),
                        },
                        employees: {
                            // Facet employees have first_name/last_name; reduxEmployees have FirstName/LastName
                            options: facetOptions.employees.length > 0
                                ? facetOptions.employees.map((e) => ({
                                    ...e,
                                    // Normalize to camelCase so TaskPageHeader's getOptionLabel works
                                    firstName: e.first_name || e.firstName || "",
                                    lastName:  e.last_name  || e.lastName  || "",
                                  }))
                                : reduxEmployees,
                            value: (
                                facetOptions.employees.find((e) => String(e.id) === currentEmp) ||
                                reduxEmployees.find((e) => String(e.userId || e.id) === currentEmp) ||
                                null
                            ),
                        },
                        ageing: {
                            options: ageingFilterOptions,
                            value: ageingFilterOptions.find((o) => o.value === currentAgeing) || null,
                        },
                        priority: {
                            options: priorityFilterOptions,
                            value: priorityFilterOptions.find((o) => o.value === currentPrio) || null,
                        },
                        targetEndDate: {
                            value: { start: currentStartDate, end: currentEndDate },
                        },
                    }}
                    onFilterChange={(type, val) => {
                        let id = type;
                        if (type === "department") id = "department";
                        if (type === "project") id = "project";
                        if (type === "group") id = "group_id";
                        if (type === "employee") id = "assignedBy";
                        if (type === "ageing") id = "ageing";

                        if (type === "targetEndDate") {
                            // val is { start, end }
                            setStagedFilters((prev) => ({
                                ...prev,
                                due_date_start: val?.start || "",
                                due_date_end: val?.end || "",
                            }));
                            return;
                        }
                        const valToSet = val ? val.id || val.userId || val.value || (typeof val === 'string' ? val : "") : "";
                        setStagedFilters((prev) => ({ ...prev, [id]: valToSet }));
                    }}
                    globalFilter={stagedSearch}
                    onGlobalFilterChange={setStagedSearch}
                    onApply={() => {
                        const newFilters = Object.entries(stagedFilters)
                            .filter(([id, value]) => value !== "" && value !== null && value !== undefined)
                            .map(([id, value]) => ({ id, value }));

                        applyFiltersAndSearch(newFilters, stagedSearch);

                        // Build query params for getStatusCount using the same mapping
                        const statusCountParams = {};
                        if (stagedSearch) statusCountParams.search = stagedSearch;
                        newFilters.forEach(({ id, value }) => {
                            const paramName = taskMeQueryParamName(id);
                            let val = value;
                            if (typeof val === "object" && val !== null) {
                                val = val.id || val.userId || val.value || "";
                            }
                            if ((id === "priority" || id === "ageing") && typeof val === "string") {
                                val = val.toLowerCase();
                            }
                            if (val !== "" && val !== "All") {
                                statusCountParams[paramName] = UUID_QUERY_PARAMS.has(paramName) ? String(val) : val;
                            }
                        });
                        fetchStatusCounts(statusCountParams);
                    }}
                    onReset={() => {
                        setStagedFilters({});
                        setStagedSearch("");
                        applyFiltersAndSearch([], "");
                    }}
                    activePills={activePills}
                />

                <div className="taskboard-table-wrapper">
                    <AppDataTable
                        columns={columns}
                        data={tasks}
                        rowCount={totalRows}
                        state={{ pagination, sorting, globalFilter, columnFilters }}
                        onPaginationChange={onPaginationChange}
                        onSortingChange={onSortingChange}
                        onGlobalFilterChange={onGlobalFilterChange}
                        onColumnFiltersChange={onColumnFiltersChange}
                        isLoading={isLoading}
                        isRefetching={isRefetching}
                        enableRowSelection={true}
                        rowSelection={rowSelection}
                        onRowSelectionChange={setRowSelection}
                        renderRowActions={renderRowActions}
                        renderTopToolbarCustomActions={({ table }) => {
                            const isSelected = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();
                            if (!isSelected) return null;
                            const count = table.getSelectedRowModel().flatRows.length;
                            return (
                                <Box sx={{ display: 'flex', gap: '8px', pl: 1 }}>
                                    <CustomButton
                                        size="small"
                                        className="task-action-btn"
                                        onClick={handleBulkComplete}
                                        loading={bulkCompleteLoading}
                                        disabled={isLoading || isRefetching}
                                        sx={{ backgroundColor: '#4caf50', borderRadius: '8px', height: '32px' }}
                                    >
                                        Mark Completed ({count})
                                    </CustomButton>
                                    <CustomButton
                                        size="small"
                                        className="task-action-btn"
                                        onClick={handleBulkDelete}
                                        loading={bulkCompleteLoading}
                                        disabled={isLoading || isRefetching}
                                        sx={{ backgroundColor: '#dc2626', borderRadius: '8px', height: '32px' }}
                                    >
                                        Delete ({count})
                                    </CustomButton>
                                </Box>
                            );
                        }}
                    />
                </div>
            </div>

            {/* Task Details Drawer */}
            <AppDrawer
                open={showTaskDrawer}
                onClose={() => setShowTaskDrawer(false)}
                title={selectedTask?.title || "Task Details"}
            >
                <TaskDetailsModal task={selectedTask} variant="drawer" open={showTaskDrawer} showGivenBy={true} onClose={() => setShowTaskDrawer(false)} />
            </AppDrawer>

            {/* Bulk Complete Confirmation */}
            {showBulkCompleteModal && (
                <div className="action-modal-overlay">
                    <div className="action-modal">
                        <h3>Confirm Bulk Complete</h3>
                        <p>
                            Are you sure you want to mark {bulkCompleteTasks.length} task
                            {bulkCompleteTasks.length !== 1 ? "s" : ""} as Completed?
                        </p>
                        <div className="bulk-confirm-actions">
                            <button
                                onClick={() => setShowBulkCompleteModal(false)}
                                className="taskboard-btn bulk-btn-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeBulkComplete}
                                className="taskboard-btn bulk-btn-confirm"
                                disabled={bulkCompleteLoading}
                            >
                                {bulkCompleteLoading ? "Processing…" : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Status Update Modal (extracted component) ── */}
            {showStatusModal && (
                <TaskStatusModal
                    task={actionTask}
                    userId={userId}
                    delegateOptions={delegateOptions}
                    onClose={() => {
                        setShowStatusModal(false);
                        setActionTask(null);
                    }}
                    onSuccess={(msg) => {
                        toast.success(msg);
                        setRefreshSignal(prev => prev + 1);
                        setShowStatusModal(false);
                        setActionTask(null);
                    }}
                />
            )}

            {/* Bulk Upload Modal */}
            <BulkUploadModal
                open={showBulkUpload}
                onClose={() => setShowBulkUpload(false)}
                onBulkUploadSuccess={() => {
                    setShowBulkUpload(false);
                    toast.success("Bulk upload successful!");
                    setRefreshSignal(prev => prev + 1);
                }}
            />

            {/* Add Task Modal */}
            {showAddTask && (
                <AddTaskModal
                    open={showAddTask}
                    onClose={() => setShowAddTask(false)}
                    onTaskCreated={() => {
                        setRefreshSignal(prev => prev + 1);
                    }}
                />
            )}
        </>
    );
};

export default TaskBoard;
